import React, { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../api";

const COUNTRIES = [
  { code: "DE", de: "Deutschland", en: "Germany" },
  { code: "AT", de: "Österreich", en: "Austria" },
  { code: "CH", de: "Schweiz", en: "Switzerland" },
  { code: "IT", de: "Italien", en: "Italy" },
  { code: "FR", de: "Frankreich", en: "France" },
  { code: "OTHER", de: "Anderes Land", en: "Other country" },
];

const T = {
  de: {
    title: "Verleih-Anmeldung",
    subtitle: "Bitte trag dich kurz ein — du erhältst einen persönlichen Code, mit dem dir das Personal das Material zuweist.",
    salutation: "Anrede",
    select: "Bitte wählen",
    mr: "Herr", mrs: "Frau", div: "Divers",
    firstName: "Vorname",
    lastName: "Nachname",
    country: "Land",
    email: "E-Mail",
    emailConfirm: "E-Mail bestätigen",
    phone: "Handynummer",
    phoneHint: "Falls wir dich erreichen müssen",
    submit: "Code generieren",
    processing: "Wird gesendet…",
    successTitle: "Geschafft!",
    successHello: "Hi",
    successCode: "Dein persönlicher Code:",
    successHint1: "Mach einen Screenshot oder notiere den Code.",
    successHint2: "Zeige ihn dem Personal an der Ausgabe — sie weisen dir dann das Material zu.",
    newRequest: "Weitere Anmeldung starten",
    error: "Fehler",
    emailMismatch: "Die E-Mail-Adressen stimmen nicht überein.",
    required: "Pflichtfeld",
  },
  en: {
    title: "Rental registration",
    subtitle: "Fill in your details — you'll receive a personal code that staff will use to assign your equipment.",
    salutation: "Salutation",
    select: "Please select",
    mr: "Mr.", mrs: "Ms.", div: "Diverse",
    firstName: "First name",
    lastName: "Last name",
    country: "Country",
    email: "Email",
    emailConfirm: "Confirm email",
    phone: "Mobile number",
    phoneHint: "In case we need to reach you",
    submit: "Generate code",
    processing: "Submitting…",
    successTitle: "Done!",
    successHello: "Hi",
    successCode: "Your personal code:",
    successHint1: "Take a screenshot or write down the code.",
    successHint2: "Show it to staff at the rental desk — they'll assign your equipment.",
    newRequest: "Start another registration",
    error: "Error",
    emailMismatch: "Email addresses do not match.",
    required: "Required",
  },
};

export default function CustomerSelfService() {
  const [lang, setLang] = useState("de");
  const t = T[lang];
  const [form, setForm] = useState({
    salutation: "",
    firstName: "",
    lastName: "",
    country: "",
    email: "",
    emailConfirm: "",
    phone: "",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.email !== form.emailConfirm) {
      setError(t.emailMismatch);
      return;
    }
    setLoading(true);
    try {
      const data = await api.publicCreateRental({
        salutation: form.salutation || null,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        country: form.country || null,
        customer_email: form.email.trim(),
        customer_email_confirm: form.emailConfirm.trim(),
        phone: form.phone.trim() || null,
      });
      setResult(data);
    } catch (err) {
      setError(err.message || "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({ salutation: "", firstName: "", lastName: "", country: "", email: "", emailConfirm: "", phone: "" });
  };

  if (result) {
    return (
      <div className="min-h-screen bg-fh-gray-light flex items-start justify-center py-10 px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img src={`${process.env.PUBLIC_URL}/fh_logo_circle.png`} alt="Freeheeler" className="h-16 w-16 logo-clean" />
          </div>
          <div className="card overflow-hidden">
            <div className="bg-fh-red text-white px-6 py-4 text-center">
              <div className="text-sm uppercase tracking-wider opacity-90">{t.successTitle}</div>
              <div className="font-semibold text-lg">{t.successHello}, {result.first_name}!</div>
            </div>
            <div className="p-6 space-y-4 text-center">
              <div className="text-sm text-fh-gray">{t.successCode}</div>
              <div className="bg-white border-2 border-dashed border-fh-red/40 rounded-lg p-4 flex flex-col items-center gap-3">
                <QRCodeCanvas
                  value={result.rental_id}
                  size={200}
                  level="H"
                  includeMargin={false}
                  imageSettings={{
                    src: `${process.env.PUBLIC_URL}/fh_logo_circle.png`,
                    height: 44,
                    width: 44,
                    excavate: true,
                  }}
                />
                <div className="font-mono font-bold text-xl tracking-wider text-fh-black">
                  {result.rental_id}
                </div>
              </div>
              <div className="text-sm text-fh-black space-y-1">
                <p>📸 {t.successHint1}</p>
                <p>👋 {t.successHint2}</p>
              </div>
              <button onClick={reset} className="btn-secondary w-full">{t.newRequest}</button>
            </div>
          </div>
          <p className="text-xs text-fh-gray text-center mt-6">freeheeler.eu</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fh-gray-light flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-2">
          <div className="inline-flex rounded-md overflow-hidden border border-gray-300 bg-white text-xs font-semibold">
            {["de", "en"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`px-3 py-1.5 ${lang === code ? "bg-fh-red text-white" : "text-fh-gray hover:bg-fh-gray-light"}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center mb-6">
          <img src={`${process.env.PUBLIC_URL}/fh_logo_circle.png`} alt="Freeheeler" className="h-16 w-16 logo-clean" />
          <h1 className="mt-3 text-2xl font-bold">{t.title}</h1>
          <p className="text-sm text-fh-gray text-center mt-2 max-w-sm">{t.subtitle}</p>
        </div>

        <form onSubmit={onSubmit} className="card p-5 space-y-4">
          {error && (
            <div className="bg-fh-red/10 border-l-4 border-fh-red px-3 py-2 rounded text-sm text-fh-red-dark">
              {error}
            </div>
          )}
          <div>
            <label className="label-field">{t.salutation}</label>
            <select className="input-field" value={form.salutation} onChange={(e) => set("salutation", e.target.value)}>
              <option value="">{t.select}</option>
              <option value="Herr">{t.mr}</option>
              <option value="Frau">{t.mrs}</option>
              <option value="Divers">{t.div}</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-field">{t.firstName} *</label>
              <input className="input-field" required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
            </div>
            <div>
              <label className="label-field">{t.lastName} *</label>
              <input className="input-field" required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label-field">{t.country}</label>
            <select className="input-field" value={form.country} onChange={(e) => set("country", e.target.value)}>
              <option value="">{t.select}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c[lang]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-field">{t.email} *</label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              className="input-field"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>
          <div>
            <label className="label-field">{t.emailConfirm} *</label>
            <input
              type="email"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              className="input-field"
              required
              value={form.emailConfirm}
              onChange={(e) => set("emailConfirm", e.target.value)}
              onPaste={(e) => e.preventDefault()}
            />
          </div>
          <div>
            <label className="label-field">{t.phone}</label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="input-field"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value.replace(/[^0-9+\s\-()]/g, ""))}
              placeholder="+49 …"
            />
            <p className="text-xs text-fh-gray mt-1">{t.phoneHint}</p>
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? t.processing : t.submit}
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/login" className="text-xs text-fh-gray hover:text-fh-black">Mitarbeiter-Login →</Link>
        </div>
      </div>
    </div>
  );
}
